import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Badge,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Center,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { SearchIcon, EditIcon, DeleteIcon, StarIcon } from '@chakra-ui/icons';
import { FiMoreVertical, FiTrash2, FiEdit } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Category } from '../../types/game.ts';
import { auth } from '../../config/firebase.ts';
import CategoryCreator from './CategoryCreator.tsx';
import './CategoryComponents.css';

interface CategoryMyCreationsProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
}

const MotionCard = motion(Card);

const CategoryMyCreations: React.FC<CategoryMyCreationsProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [myCategories, setMyCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 12;
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  
  // Disclosures pour les modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  // Configuration de l'API URL - utilise la variable d'environnement ou fallback
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

  // Récupérer le token Firebase
  useEffect(() => {
    const getCurrentUserToken = async () => {
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error('Erreur lors de la récupération du token:', error);
        }
      }
    };

    if (isOpen && user) {
      getCurrentUserToken();
    }
  }, [isOpen, user]);

  // Charger les catégories de l'utilisateur
  const loadMyCategories = async () => {
    if (!userToken) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      params.append('sortBy', 'created_at');
      params.append('sortOrder', 'desc');

      const response = await fetch(`${API_URL}/api/categories/my-categories?${params}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🏠 Mes catégories chargées:', data);
        setMyCategories(data.categories || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      } else {
        console.error('Erreur lors du chargement des catégories:', response.status);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données quand la modal s'ouvre ou quand la page/recherche change
  useEffect(() => {
    if (isOpen && userToken) {
      loadMyCategories();
    }
  }, [isOpen, userToken, currentPage]);

  // Réinitialiser la page lors d'une recherche
  useEffect(() => {
    if (searchTerm !== '') {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Déclencher la recherche après un délai (debounce)
  useEffect(() => {
    if (isOpen && userToken) {
      const timer = setTimeout(() => {
        if (currentPage === 1) {
          loadMyCategories();
        } else {
          setCurrentPage(1);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchTerm]);

  // Fonction pour ouvrir la modal d'édition
  const handleEdit = (category: Category) => {
    setCategoryToEdit(category);
    onEditOpen();
  };

  // Fonction pour fermer la modal d'édition et recharger
  const handleEditClose = () => {
    onEditClose();
    setCategoryToEdit(null);
    // Recharger les catégories après édition
    if (userToken) {
      loadMyCategories();
    }
  };

  // Fonction pour ouvrir la confirmation de suppression
  const handleDeleteConfirm = (category: Category) => {
    setCategoryToDelete(category);
    onDeleteOpen();
  };

  // Fonction pour supprimer une catégorie
  const handleDelete = async () => {
    if (!categoryToDelete || !userToken) return;

    try {
      const response = await fetch(`${API_URL}/api/categories/${categoryToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast({
          title: 'Catégorie supprimée',
          description: `${categoryToDelete.name} a été supprimée avec succès`,
          status: 'success',
          duration: 3000,
        });
        
        // Recharger les catégories
        loadMyCategories();
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la catégorie',
        status: 'error',
        duration: 3000,
      });
    } finally {
      onDeleteClose();
      setCategoryToDelete(null);
    }
  };

  // Le filtrage est maintenant fait côté serveur via l'API
  const filteredMyCategories = myCategories;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent className="category-explorer-modal" maxH="90vh">
        <ModalHeader className="category-explorer-header">
          <VStack spacing={2} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" className="category-explorer-text">
              ⚙️ Mes Créations
            </Text>
            <Text fontSize="md" className="category-explorer-text-muted">
              Gérez vos catégories personnalisées
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton className="category-close-button" />

        <ModalBody className="category-explorer-body category-modal-body-scrollable">
          <VStack spacing={6} align="stretch">
            {/* Barre de recherche */}
            <InputGroup>
              <InputLeftElement>
                <SearchIcon color="var(--texte-secondaire)" />
              </InputLeftElement>
              <Input
                placeholder="Rechercher dans vos créations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="category-explorer-input"
              />
            </InputGroup>

            {/* Liste des catégories créées */}
            {isLoading ? (
              <Center py={10}>
                <Spinner size="xl" color="var(--bleu-electrique)" />
              </Center>
            ) : filteredMyCategories.length === 0 ? (
              <Center py={10}>
                <VStack spacing={4}>
                  <Text fontSize="lg" color="var(--texte-secondaire)">
                    {searchTerm ? 'Aucune catégorie trouvée' : 'Vous n\'avez créé aucune catégorie'}
                  </Text>
                  {!searchTerm && (
                    <Text fontSize="sm" color="var(--texte-secondaire)">
                      Cliquez sur "Créer" pour créer votre première catégorie
                    </Text>
                  )}
                </VStack>
              </Center>
            ) : (
              <VStack spacing={6} align="stretch">
              <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
                {filteredMyCategories.map((category) => (
                  <MotionCard
                    key={category.id}
                    className="category-explorer-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CardHeader pb={2}>
                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack spacing={2}>
                            <Text fontSize="lg" fontWeight="bold" className="category-explorer-text">
                              {category.emoji} {category.name}
                            </Text>
                            {category.is_custom && (
                              <Badge colorScheme="purple" variant="subtle">
                                Personnalisée
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="sm" className="category-explorer-text-muted">
                            Par vous
                          </Text>
                        </VStack>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                            color="var(--texte-secondaire)"
                          />
                          <MenuList 
                            bg="var(--nuit-profonde)" 
                            border="1px solid var(--gris-bleute)"
                            color="var(--texte-clair)"
                          >
                            <MenuItem 
                              icon={<FiEdit />} 
                              color="var(--texte-clair)"
                              bg="transparent"
                              _hover={{ bg: "var(--gris-bleute)", color: "white" }}
                              _focus={{ bg: "var(--gris-bleute)", color: "white" }}
                              onClick={() => handleEdit(category)}
                            >
                              Modifier
                            </MenuItem>
                            <MenuItem 
                              icon={<FiTrash2 />} 
                              color="red.400"
                              bg="transparent"
                              _hover={{ bg: "red.600", color: "white" }}
                              _focus={{ bg: "red.600", color: "white" }}
                              onClick={() => handleDeleteConfirm(category)}
                            >
                              Supprimer
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack align="start" spacing={3}>
                        <Text fontSize="sm" color="var(--texte-secondaire)" noOfLines={2}>
                          {category.description || 'Aucune description'}
                        </Text>
                        <HStack spacing={4} fontSize="sm" color="var(--texte-secondaire)">
                          <Text>🎯 {category.question_count || 0} questions</Text>
                          <Text>📅 {category.created_at ? new Date(category.created_at).toLocaleDateString() : 'Date inconnue'}</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </MotionCard>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <HStack justify="center" spacing={2} mt={4}>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    isDisabled={currentPage === 1}
                    className="category-explorer-button"
                  >
                    ←
                  </Button>
                  <Text fontSize="sm" color="var(--texte-secondaire)">
                    Page {currentPage} sur {totalPages} ({total} catégories)
                  </Text>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    isDisabled={currentPage === totalPages}
                    className="category-explorer-button"
                  >
                    →
                  </Button>
                </HStack>
              )}
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter className="category-explorer-footer">
          <Button onClick={onClose} className="category-explorer-button">
            Fermer
          </Button>
        </ModalFooter>
      </ModalContent>

      {/* Modal d'édition */}
      <CategoryCreator
        isOpen={isEditOpen}
        onClose={handleEditClose}
        onCategoryCreated={handleEditClose}
        user={user}
        editCategory={categoryToEdit}
        onCloseParentModal={onClose} // Fermer CategoryMyCreations quand CategoryCreator s'ouvre
      />

      {/* Confirmation de suppression */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay bg="blackAlpha.600">
          <AlertDialogContent
            bg="gray.800"
            color="white"
            borderColor="gray.600"
          >
            <AlertDialogHeader 
              fontSize="lg" 
              fontWeight="bold"
              color="white"
            >
              Supprimer la catégorie
            </AlertDialogHeader>

            <AlertDialogBody color="gray.200">
              Êtes-vous sûr de vouloir supprimer la catégorie "{categoryToDelete?.name}" ? 
              Cette action est irréversible et supprimera également toutes les questions associées.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button 
                ref={cancelRef} 
                onClick={onDeleteClose}
                variant="outline"
                colorScheme="gray"
                borderColor="gray.500"
                color="white"
                _hover={{ 
                  bg: "gray.700",
                  borderColor: "gray.400"
                }}
              >
                Annuler
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDelete} 
                ml={3}
                variant="solid"
                _hover={{ bg: 'red.600' }}
                _active={{ bg: 'red.700' }}
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default CategoryMyCreations;
